import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Progress,
  Dropdown,
  Select,
  Form,
  Input,
  DatePicker,
  Image
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  EyeOutlined,
  ReloadOutlined,
  FlagOutlined,
  DownOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  UserOutlined,
  UnorderedListOutlined,
  PictureOutlined
} from '@ant-design/icons';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';
import FilterPanel from '../../components/common/FilterPanel';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { PlanogramTask, TaskSearchParams, TaskStatistics, Priority, TaskStatus, PauseReason, Division, PlanType } from './task';
import { TaskService } from '../../services/taskService';
import { accountService } from '../../services/accountService'; // 导入 accountService
import StoreDisplayGroupService from '../../services/storeDisplayGroupService'; // 导入 StoreDisplayGroupService
import categoryService from '../../services/categoryService'; // 导入 categoryService
import TaskDetails from './TaskDetails';
import ProductListModal from './ProductListModal';
import FeedbackModal from './FeedbackModal';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TaskListPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<PlanogramTask[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchParams, setSearchParams] = useState<TaskSearchParams>({
    currentPage: 1,
    pageSize: 20
  });

  const [detailsVisible, setDetailsVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<PlanogramTask | null>(null);
  const [productListVisible, setProductListVisible] = useState(false);
  const [selectedTaskForProductList, setSelectedTaskForProductList] = useState<PlanogramTask | null>(null);
  const [batchAssignVisible, setBatchAssignVisible] = useState(false);
  const [selectedSectionOwner, setSelectedSectionOwner] = useState<string>('');
  const [pauseReasonModalVisible, setPauseReasonModalVisible] = useState(false);
  const [selectedPauseReason, setSelectedPauseReason] = useState<PauseReason | undefined>(undefined);
  const [selectedPauseRemark, setSelectedPauseRemark] = useState<string>(''); // 添加暂停说明状态
  const [planogramModalVisible, setPlanogramModalVisible] = useState(false);
  const [selectedTaskForPlanogram, setSelectedTaskForPlanogram] = useState<PlanogramTask | null>(null);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [selectedTaskForFeedback, setSelectedTaskForFeedback] = useState<PlanogramTask | null>(null);

  // 搜索表单相关状态
  const [displayGroupOptions, setDisplayGroupOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [divisionOptions, setDivisionOptions] = useState<Array<{ value: string; label: string }>>([]); // 修改为动态获取
  const [hasLoadedOptions, setHasLoadedOptions] = useState(false);

  // 计划类型选项
  const planTypeOptions: PlanType[] = ['Major Plans', 'Regular Plans', 'Minor Plans', 'Urgent Plans', 'Special Plans'];
  
  // 优先级选项
  const priorityOptions: Priority[] = ['High', 'Medium', 'Low'];
  
  // 状态选项
  const statusOptions: TaskStatus[] = ['Not Started', 'In Progress', 'Paused', 'Completed'];
  
  // Division选项 - 移除硬编码的数组

  // 移除单独的 form 实例声明，让 FilterPanel 自己管理

  // 移除从 mock 文件导入的 accounts
  // import { accounts } from '../../mock/mockAccountData';
  
  // 添加 accounts 状态
  const [accounts, setAccounts] = useState<Array<{ value: string; label: string }>>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  // 添加获取账户选项的函数
  const loadAccountOptions = async () => {
    setAccountsLoading(true);
    try {
      const response = await accountService.getAccountOptions();
      if (response.success && response.data) {
        setAccounts(response.data);
      }
    } catch (error) {
      console.error('Failed to load account options:', error);
      message.error(t('taskManagement.list.messages.loadAccountOptionsFailed'));
    } finally {
      setAccountsLoading(false);
    }
  };

  // 在 useEffect 中加载任务和选项数据，但不加载账户选项
  useEffect(() => {
    loadTasks();
    // 只在第一次加载时获取选项数据
    if (!hasLoadedOptions) {
      loadOptions(true);
      setHasLoadedOptions(true);
    } else {
      // 后续只加载陈列计划数据
      loadOptions(false);
    }
  }, []);

  // 当搜索参数变化时重新加载任务数据
  useEffect(() => {
    loadTasks();
  }, [searchParams]);

  // 修改loadOptions函数，添加一个参数来控制是否加载门店数据
  const loadOptions = async (loadShops = true) => {
    try {
      // 创建一个数组来存储需要执行的Promise
      const promises = [];
      
      // 添加获取 displayGroupOptions 的逻辑
      promises.push(StoreDisplayGroupService.getOptionByUserStore(""));
      
      // 添加获取 divisionOptions 的逻辑
      promises.push(categoryService.listDivision());
      
      // 等待所有Promise完成
      const results = await Promise.all(promises);
      
      // 根据是否加载了门店数据来处理结果
      let displayGroupRes = results[0];
      let divisionRes = results[1];
      
      
      // 处理 displayGroup 数据
      if (displayGroupRes && displayGroupRes.success && displayGroupRes.data) {
        setDisplayGroupOptions(displayGroupRes.data);
      }
      
      // 处理 division 数据
      if (divisionRes && divisionRes.success && divisionRes.data) {
        // 根据返回的数据结构，将 value 和 label 映射为 value 和 label
        const options = divisionRes.data.map((item: any) => ({
          value: item.value,
          label: item.label
        }));
        setDivisionOptions(options);
      }
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await TaskService.getTaskList4Store(searchParams);
      if (response.success && response.data) {
        // 对任务数据进行排序
        const sortedTasks = response.data.tableData;
        
        setTasks(sortedTasks);
        setTotal(response.data.paging.totalCount);
      } else {
        message.error(response.errorMessage || t('taskManagement.list.messages.fetchFailed'));
      }
    } catch (error) {
      message.error(t('taskManagement.list.messages.networkError'));
    } finally {
      setLoading(false);
    }
  };

  // 批量标记优先级
  const handleBatchPriorityMark = async (priority: Priority) => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('taskManagement.list.messages.selectFirst'));
      return;
    }

    try {
      const taskCodes = selectedRowKeys as string[];
      const response = await TaskService.batchMarkPriority(taskCodes, priority);
      
      if (response.success) {
        setSelectedRowKeys([]);
        loadTasks();
        message.success(t('taskManagement.list.messages.priorityMarked', { count: selectedRowKeys.length, priority }));
      } else {
        message.error(response.errorMessage || t('taskManagement.list.messages.priorityMarkFailed'));
      }
    } catch (error) {
      message.error(t('taskManagement.list.messages.priorityMarkFailed'));
    }
  };

  // 显示暂停原因选择对话框
  const showPauseReasonModal = () => {
    setPauseReasonModalVisible(true);
    setSelectedPauseReason(undefined);
    setSelectedPauseRemark(''); // 重置暂停说明
  };

  // 处理暂停原因确认
  const handlePauseReasonConfirm = async () => {
    if (!selectedPauseReason) {
      message.warning(t('taskManagement.list.messages.selectPauseReason'));
      return;
    }

    try {
      const taskCodes = selectedRowKeys as string[];
      // 修改调用以传递暂停说明
      const response = await TaskService.batchPauseTasks(taskCodes, selectedPauseReason, selectedPauseRemark);
      
      if (response.success) {
        setSelectedRowKeys([]);
        setPauseReasonModalVisible(false);
        setSelectedPauseRemark(''); // 清空暂停说明
        loadTasks();
        message.success(t('taskManagement.list.messages.tasksPaused', { count: selectedRowKeys.length }));
      } else {
        message.error(response.errorMessage || t('taskManagement.list.messages.pauseFailed'));
      }
    } catch (error) {
      message.error(t('taskManagement.list.messages.pauseFailed'));
    }
  };

  // 批量暂停任务
  const handleBatchPause = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('taskManagement.list.messages.selectPauseFirst'));
      return;
    }

    // 检查选中的任务是否都支持暂停操作
    const selectedTasks = tasks.filter(task => selectedRowKeys.includes(task.taskCode));
    const invalidTasks = selectedTasks.filter(task => 
      task.status !== 'Not Started' && task.status !== 'In Progress'
    );

    if (invalidTasks.length > 0) {
      message.warning(t('taskManagement.list.messages.onlyPausableStatus'));
      return;
    }

    // 显示暂停原因选择对话框
    showPauseReasonModal();
  };

  // 批量恢复任务
  const handleBatchResume = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('taskManagement.list.messages.selectResumeFirst'));
      return;
    }

    // 检查选中的任务是否都是已暂停的任务
    const selectedTasks = tasks.filter(task => selectedRowKeys.includes(task.taskCode));
    const invalidTasks = selectedTasks.filter(task => !task.isPaused);

    if (invalidTasks.length > 0) {
      message.warning(t('taskManagement.list.messages.onlyResumePaused'));
      return;
    }

    // 二次确认提示
    Modal.confirm({
      title: t('taskManagement.list.batchActions.confirmResume.title'),
      content: t('taskManagement.list.batchActions.confirmResume.content', { count: selectedRowKeys.length }),
      okText: t('taskManagement.list.batchActions.confirmResume.okText'),
      cancelText: t('taskManagement.list.batchActions.confirmResume.cancelText'),
      onOk: async () => {
        try {
          const taskCodes = selectedRowKeys as string[];
          const response = await TaskService.batchResumeTasks(taskCodes);
          
          if (response.success) {
            setSelectedRowKeys([]);
            loadTasks();
            message.success(t('taskManagement.list.messages.tasksResumed', { count: selectedRowKeys.length }));
          } else {
            message.error(response.errorMessage || t('taskManagement.list.messages.resumeFailed'));
          }
        } catch (error) {
          message.error(t('taskManagement.list.messages.resumeFailed'));
        }
      }
    });
  };

  // 批量指派Section Owner
  const handleBatchAssign = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('taskManagement.list.messages.selectFirst'));
      return;
    }

    // 检查选中的任务是否都不是已完成状态
    const selectedTasks = tasks.filter(task => selectedRowKeys.includes(task.taskCode));
    const completedTasks = selectedTasks.filter(task => task.status === 'Completed');

    if (completedTasks.length > 0) {
      message.warning(t('taskManagement.list.messages.completedTasksCannotAssign'));
      return;
    }

    // 在打开弹框前加载账户选项
    await loadAccountOptions();
    setBatchAssignVisible(true);
  };

  // 确认批量指派
  const handleConfirmBatchAssign = async () => {
    if (!selectedSectionOwner) {
      message.warning(t('taskManagement.list.messages.selectSectionOwner'));
      return;
    }

    try {
      const taskCodes = selectedRowKeys as string[];
      const response = await TaskService.batchAssignSectionOwner(taskCodes, selectedSectionOwner);
      
      if (response.success) {
        setSelectedRowKeys([]);
        setBatchAssignVisible(false);
        setSelectedSectionOwner('');
        loadTasks();
        message.success(t('taskManagement.list.messages.assignSuccess', { count: selectedRowKeys.length }));
      } else {
        message.error(response.errorMessage || t('taskManagement.list.messages.assignFailed'));
      }
    } catch (error) {
      message.error(t('taskManagement.list.messages.assignFailed'));
    }
  };

  // 获取优先级颜色
  const getPriorityColor = (priority: Priority): string => {
    const colorMap = {
      'High': 'red',
      'Medium': 'orange',
      'Low': 'green'
    };
    return colorMap[priority];
  };

  // 获取状态颜色
  const getStatusColor = (status: TaskStatus): string => {
    const colorMap = {
      'Not Started': 'default',
      'In Progress': 'processing',
      'Paused': 'warning',
      'Completed': 'success'
    };
    return colorMap[status];
  };

  // 表格列定义
  const columns: ColumnsType<PlanogramTask> = [
    {
      title: t('taskManagement.list.columns.taskCode'),
      dataIndex: 'taskCode',
      key: 'taskCode',
      width: 120,
      fixed: 'left',
    },
    // Removed task name column
    {
      title: t('taskManagement.list.columns.shop'),
      dataIndex: 'shop',
      key: 'shop',
      width: 120,
    },
    {
      title: t('taskManagement.search.displayGroup'),
      dataIndex: 'displayGroup',
      key: 'displayGroup',
      width: 120,
    },
    {
      title: t('taskManagement.search.division'),
      dataIndex: 'division',
      key: 'division',
      width: 100,
      render: (division: string) => (
        <Tag color={division === 'Dry Food' ? 'blue' : division === 'Fresh Food' ? 'green' : 'orange'}>
          {division}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.search.shelf'),
      dataIndex: 'shelf',
      key: 'shelf',
      width: 100,
    },
        {
      title: 'Section Owner', // Section Owner
      dataIndex: 'sectionOwner',
      key: 'sectionOwner',
      width: 120,
      render: (sectionOwner: string) => (
        <span>{sectionOwner || '-'}</span>
      ),
    },
    {
      title: t('taskManagement.list.columns.isOverdue'), // 是否延期
      dataIndex: 'isOverdue',
      key: 'isOverdue',
      width: 100,
      render: (isOverdue: boolean) => (
        <Tag color={isOverdue ? 'red' : 'green'}>
          {isOverdue ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.search.planogramPlan'),
      dataIndex: 'planogramPlan',
      key: 'planogramPlan',
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('taskManagement.list.columns.planType'),
      dataIndex: 'planType',
      key: 'planType',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: Priority) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: TaskStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: t('taskManagement.details.status.pending'), // 暂停标识
      dataIndex: 'isPaused',
      key: 'isPaused',
      width: 100,
      render: (isPaused: boolean) => (
        <Tag color={isPaused ? 'orange' : 'green'}>
          {isPaused ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.hasFeedback'),
      dataIndex: 'hasFeedback',
      key: 'hasFeedback',
      width: 100,
      render: (hasFeedback: boolean) => (
        <Tag color={hasFeedback ? 'orange' : 'green'}>
          {hasFeedback ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.feedbackCompleted'),
      dataIndex: 'feedbackCompleted',
      key: 'feedbackCompleted',
      width: 180,
      render: (feedbackCompleted: boolean | null) => (
        feedbackCompleted !== null ? (
          <Tag color={feedbackCompleted ? 'orange' : 'green'}>
            {feedbackCompleted ? t('common.yes') : t('common.no')}
          </Tag>
        ) : (
          <span>-</span>
        )
      ),
    },
    {
      title: t('taskManagement.list.columns.pauseReason'),
      dataIndex: 'pauseReason',
      key: 'pauseReason',
      width: 160,
      render: (pauseReason: PauseReason | undefined, record: PlanogramTask) => {
        if (record.isPaused && pauseReason) {
          const reasonKey = pauseReason.toLowerCase().replace(/\s+/g, '');
          return <Tag color="orange">{t(`taskManagement.pauseReasons.${reasonKey}`)}</Tag>;
        }
        return '-';
      },
    },
    {
      title: t('taskManagement.list.pauseModal.pauseRemark'),
      dataIndex: 'pauseRemark',
      key: 'pauseRemark',
      width: 160,
    },
    {
      title: t('taskManagement.details.tabs.moveOut') + " " + t('common.progress'), // 下架进度
      dataIndex: 'moveOutProgress',
      key: 'moveOutProgress',
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('taskManagement.details.tabs.moveIn')+ " " + t('common.progress'), // 移入进度
      dataIndex: 'moveInProgress',
      key: 'moveInProgress',
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('taskManagement.details.tabs.rearrange') + " " + t('common.progress'), // 调整进度
      dataIndex: 'rearrangeProgress',
      key: 'rearrangeProgress', 
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('taskManagement.details.tabs.newItem') + " " + t('common.progress'), // 新商品进度
      dataIndex: 'newItemProgress',
      key: 'newItemProgress',
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('common.totalItems'), // 待处理商品总件数
      dataIndex: 'totalItemsCount',
      key: 'totalItemsCount',
      width: 140,
      render: (count: number) => (
        <span style={{ fontWeight: 'bold' }}>{count}</span>
      ),
    },
    // {
    //   title: t('common.totalWeight'), // 待处理商品总重量
    //   dataIndex: 'totalWeight',
    //   key: 'totalWeight',
    //   width: 140,
    //   render: (weight: number) => (
    //     <span style={{ fontWeight: 'bold' }}>{weight.toFixed(2)} kg</span>
    //   ),
    // },
    {
      title: t('taskManagement.list.columns.planStartDate'),
      dataIndex: 'planStartDate',
      key: 'planStartDate',
      width: 120,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.planEndDate'),
      dataIndex: 'planEndDate',
      key: 'planEndDate',
      width: 120,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.generateTime'),
      dataIndex: 'generateTime',
      key: 'generateTime',
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.completeTime'),
      dataIndex: 'completeTime',
      key: 'completeTime',
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },

    // {
    //   title: t('common.createBy'), // 创建人
    //   dataIndex: 'createBy',
    //   key: 'createBy',
    //   width: 100,
    // },
    {
      title: t('taskManagement.list.columns.actions'),
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('taskManagement.list.actions.view')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title={t('taskManagement.list.actions.productList')}>
            <Button
              type="text"
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => handleViewProductList(record)}
            />
          </Tooltip>
          <Tooltip title={t('taskManagement.list.actions.viewPlanogram')}>
            <Button
              type="text"
              size="small"
              icon={<PictureOutlined />}
              onClick={() => handleViewPlanogram(record)}
            />
          </Tooltip>
          {record.hasFeedback && ( // 只有当任务有feedback时才显示查看feedback按钮
            <Tooltip title={t('taskManagement.list.actions.viewFeedback')}>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewFeedback(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    const newParams = {
      ...searchParams,
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    };
    setSearchParams(newParams);
  };

  // 查看详情
  const handleViewDetails = (task: PlanogramTask) => {
    setCurrentTask(task);
    setDetailsVisible(true);
  };

  // 查看商品清单
  const handleViewProductList = (task: PlanogramTask) => {
    setSelectedTaskForProductList(task);
    setProductListVisible(true);
  };

  // 查看陈列图
  const handleViewPlanogram = (task: PlanogramTask) => {
    setSelectedTaskForPlanogram(task);
    setPlanogramModalVisible(true);
  };

  // 查看feedback
  const handleViewFeedback = (task: PlanogramTask) => {
    setSelectedTaskForFeedback(task);
    setFeedbackModalVisible(true);
  };

  // 行选择配置
  const rowSelection: TableRowSelection<PlanogramTask> = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: PlanogramTask) => ({
      disabled: record.status === 'Completed', // 已完成的任务不能选择
    }),
  };

    const formatDateTime = (timestamp: number): string => {
      if (!timestamp) {
        return '';
      }
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };
  
  // 处理搜索
  const handleSearch = (values: any) => {
    // 移除值为 undefined 或空数组的字段
    const filteredValues = Object.fromEntries(
      Object.entries(values).filter(([_, value]) => 
        value !== undefined && 
        value !== null && 
        !(Array.isArray(value) && value.length === 0) &&
        !(typeof value === 'string' && value.trim() === '')
      )
    );
    
    const searchParams: TaskSearchParams = {
      ...filteredValues,
      planStartDate: values.planStartDate ? [
        values.planStartDate[0].valueOf(),
        values.planStartDate[1].valueOf()
      ] : undefined,
      planEndDate: values.planEndDate ? [
        values.planEndDate[0].valueOf(),
        values.planEndDate[1].valueOf()
      ] : undefined,
      generateTime: values.generateTime ? [
        values.generateTime[0].valueOf(), // 转换为时间戳
        values.generateTime[1].valueOf()  // 转换为时间戳
      ] : undefined,
      completeTime: values.completeTime ? [
        values.completeTime[0].valueOf(), // 转换为时间戳
        values.completeTime[1].valueOf()  // 转换为时间戳
      ] : undefined,
      currentPage: 1,
      pageSize: 20
    };
    
    // 更新搜索参数并重新加载数据
    setSearchParams(searchParams);
  };

  // 处理重置
  const handleReset = () => {
    // 不再需要手动重置表单，FilterPanel 会处理
    // setDisplayGroupOptions([]);
    const resetParams: TaskSearchParams = {
      currentPage: 1,
      pageSize: 20
    };
    setSearchParams(resetParams);
  };

  // 优先级菜单项
  const priorityMenuItems = [
    {
      key: 'High',
      label: (
        <Space>
          <Tag color="red">High</Tag>
          {t('taskManagement.list.batchActions.highPriority')}
        </Space>
      ),
    },
    {
      key: 'Medium',
      label: (
        <Space>
          <Tag color="orange">Medium</Tag>
          {t('taskManagement.priority.medium')}
        </Space>
      ),
    },
    {
      key: 'Low',
      label: (
        <Space>
          <Tag color="green">Low</Tag>
          {t('taskManagement.list.batchActions.lowPriority')}
        </Space>
      ),
    },
  ];

  // 渲染过滤面板
  const renderFilterPanel = () => (
    <FilterPanel
      onFilter={handleSearch}
      onReset={handleReset}
      title={t('taskManagement.search.title')}
      className="task-search-panel"
      loading={loading}
    >
      <Col span={6}>
        <Form.Item name="taskCode" label={t('taskManagement.search.taskCode')}>
          <Input placeholder={t('taskManagement.search.placeholders.taskCode')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="status" label={t('taskManagement.search.status')}>
          <Select placeholder={t('taskManagement.search.placeholders.status')} allowClear>
            {statusOptions.map(status => (
              <Option key={status} value={status}>{status}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="division" label={t('taskManagement.search.division')}>
          <Select placeholder={t('taskManagement.search.placeholders.division')} allowClear>
            {divisionOptions.map(division => (
              <Option key={division.value} value={division.value}>{division.label}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="displayGroup" label={t('taskManagement.search.displayGroup')}>
          <Select placeholder={t('taskManagement.search.placeholders.displayGroup')} allowClear>
            {displayGroupOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="shelf" label={t('taskManagement.search.shelf')}>
          <Input placeholder={t('taskManagement.search.placeholders.shelf')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planogramPlan" label={t('taskManagement.search.planogramPlan')}>
             <Input placeholder={t('taskManagement.search.planogramPlan')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planType" label={t('taskManagement.search.planType')}>
          <Select placeholder={t('taskManagement.search.placeholders.planType')} allowClear>
            {planTypeOptions.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="priority" label={t('taskManagement.search.priority')}>
          <Select placeholder={t('taskManagement.search.placeholders.priority')} allowClear>
            {priorityOptions.map(priority => (
              <Option key={priority} value={priority}>{priority}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planStartDate" label={t('taskManagement.search.planStartDate')}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planEndDate" label={t('taskManagement.search.planEndDate')}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="generateTime" label={t('taskManagement.search.generateTime')}>
          <RangePicker style={{ width: '100%' }} showTime />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="completeTime" label={t('taskManagement.search.completeTime')}>
          <RangePicker style={{ width: '100%' }} showTime />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="hasFeedback" label={t('taskManagement.search.hasFeedback')}>
          <Select placeholder={t('taskManagement.search.placeholders.hasFeedback')} allowClear>
            <Option value="true">{t('common.yes')}</Option>
            <Option value="false">{t('common.no')}</Option>
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="hasDelay" label={t('taskManagement.list.columns.isOverdue')}>
          <Select placeholder={t('taskManagement.list.columns.isOverdue')} allowClear>
            <Option value="true">{t('common.yes')}</Option>
            <Option value="false">{t('common.no')}</Option>
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="feedbackCompleted" label={t('taskManagement.search.feedbackCompleted')}>
          <Select placeholder={t('taskManagement.search.placeholders.feedbackCompleted')} allowClear>
            <Option value="true">{t('common.yes')}</Option>
            <Option value="false">{t('common.no')}</Option>
          </Select>
        </Form.Item>
      </Col>
    </FilterPanel>
  );

  // 批量指派Section Owner对话框
  const renderBatchAssignModal = () => (
    <Modal
      title={t('taskManagement.list.assignModal.title')}
      open={batchAssignVisible}
      onOk={handleConfirmBatchAssign}
      onCancel={() => {
        setBatchAssignVisible(false);
        setSelectedSectionOwner('');
      }}
      okText={t('taskManagement.list.assignModal.confirmAssign')}
      cancelText={t('common.cancel')}
    >
      <div style={{ marginBottom: 16 }}>
        <p>{t('taskManagement.list.assignModal.selectedTasks', { count: selectedRowKeys.length })}</p>
        <p>{t('taskManagement.list.assignModal.selectOwner')}</p>
        <Select
          style={{ width: '100%' }}
          placeholder={t('taskManagement.list.assignModal.ownerPlaceholder')}
          value={selectedSectionOwner}
          onChange={setSelectedSectionOwner}
          showSearch
          loading={accountsLoading}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={accounts.map(account => ({
            ...account,
            label: `${account.value} - ${account.label}`
          }))}
        />
      </div>
    </Modal>
  );

  return (
    <div>
      {/* 搜索过滤器 */}
      {renderFilterPanel()}

      {/* 任务列表 */}
      <Card style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: 'priorityHigh',
                label: t('taskManagement.list.batchActions.highPriority'),
                icon: <FlagOutlined />,
                onClick: () => handleBatchPriorityMark('High'),
              },
              {
                key: 'priorityMedium',
                label: t('taskManagement.priority.medium'),
                icon: <FlagOutlined />,
                onClick: () => handleBatchPriorityMark('Medium'),
              },
              {
                key: 'priorityLow',
                label: t('taskManagement.list.batchActions.lowPriority'),
                icon: <FlagOutlined />,
                onClick: () => handleBatchPriorityMark('Low'),
              },
              {
                key: 'pause',
                label: t('taskManagement.list.batchActions.batchPause'),
                icon: <PauseOutlined />,
                onClick: handleBatchPause,
              },
              {
                key: 'resume',
                label: t('taskManagement.list.batchActions.batchResume'),
                icon: <PlayCircleOutlined />,
                onClick: handleBatchResume,
              },
              {
                key: 'batchAssign',
                label: t('taskManagement.list.batchActions.batchAssign'),
                icon: <UserOutlined />,
                onClick: handleBatchAssign,
              }
            ]}
            onClearSelection={() => setSelectedRowKeys([])}
          />
        </div>
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="taskCode"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1200, y: 500 }}
          pagination={{
            current: searchParams.currentPage,
            pageSize: searchParams.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('taskManagement.list.messages.paginationTotal', { start: range[0], end: range[1], total }),
            pageSizeOptions: ['20', '50', '100'],
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 任务详情弹窗 */}
      <Modal
        title={t('taskManagement.list.messages.taskDetailsTitle')}
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        width={1200}
        footer={null}
        destroyOnClose={true}
      >
        {currentTask && (
          <TaskDetails key={currentTask.taskCode} task={currentTask} />
        )}
      </Modal>

      {/* 商品清单弹窗 */}
      <ProductListModal
        visible={productListVisible}
        onClose={() => setProductListVisible(false)}
        task={selectedTaskForProductList}
      />

      {/* 暂停原因选择对话框 */}
      <Modal
         title={t('taskManagement.list.pauseModal.title')}
         open={pauseReasonModalVisible}
         onOk={handlePauseReasonConfirm}
         onCancel={() => {
           setPauseReasonModalVisible(false);
           setSelectedPauseReason(undefined);
           setSelectedPauseRemark(''); // 清空暂停说明
           setSelectedRowKeys([]); // 取消时清除已选择的行
         }}
         okText={t('taskManagement.list.pauseModal.confirmPause')}
         cancelText={t('common.cancel')}
       >
         <div style={{ marginBottom: 16 }}>
           <p>{t('taskManagement.list.pauseModal.selectedTasks', { count: selectedRowKeys.length })}</p>
           <p style={{ color: '#ff4d4f', marginBottom: 16 }}>{t('taskManagement.list.pauseModal.warning')}</p>
           <p>{t('taskManagement.list.pauseModal.selectReason')}</p>
          <Select
            style={{ width: '100%' }}
            placeholder={t('taskManagement.list.pauseModal.reasonPlaceholder')}
            value={selectedPauseReason}
            onChange={setSelectedPauseReason}
            options={[
              { value: 'Waiting for GR', label: 'Waiting for GR' },
              { value: 'Waiting for replenish', label: 'Waiting for replenish' },
              { value: 'Missing top stock', label: 'Missing top stock' },
              { value: 'No resources', label: 'No resources' },
              { value: 'Others', label: 'Others' }
            ]}
          />
          <p style={{ marginTop: 16 }}>{t('taskManagement.list.pauseModal.pauseRemark')}</p>
          <Input.TextArea
            placeholder={t('taskManagement.list.pauseModal.pauseRemarkPlaceholder')}
            value={selectedPauseRemark}
            onChange={(e) => setSelectedPauseRemark(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </div>
      </Modal>

      {/* 批量指派Section Owner对话框 */}
      {renderBatchAssignModal()}

      {/* 查看陈列图对话框 */}
      <Modal
        title={t('taskManagement.list.planogramModal.title')}
        open={planogramModalVisible}
        onCancel={() => {
          setSelectedTaskForPlanogram(null);
          setPlanogramModalVisible(false);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setSelectedTaskForPlanogram(null);
            setPlanogramModalVisible(false);
          }}>
            {t('common.close')}
          </Button>
        ]}
        width={800}
        destroyOnClose={true}
      >
        {planogramModalVisible && selectedTaskForPlanogram && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Image
              key={selectedTaskForPlanogram.taskCode}
              src={selectedTaskForPlanogram.shelfPicture}
              style={{ width: '100%', height: 550, objectFit: 'cover', border: '1px solid #d9d9d9' }}
              placeholder
            />
            <p style={{ fontSize: '14px', color: '#666', marginTop: '16px' }}>
              {t('taskManagement.list.planogramModal.taskCode')}: {selectedTaskForPlanogram.taskCode}
            </p>
          </div>
        )}
      </Modal>

      {/* 查看Feedback对话框 */}
      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={() => {
          setFeedbackModalVisible(false);
          setSelectedTaskForFeedback(null);
        }}
        task={selectedTaskForFeedback}
      />
    </div>
  );
};

export default TaskListPage;